import React from "react";
import { motion } from "framer-motion";
import {
  Hero,
  SectionHeader,
  CTASection,
  styles,
  animations,
} from "../../../components/landing/SharedComponent";
import { Link } from "react-router-dom";

const Research = () => {
  // Three main research areas
  const researchAreas = [
    {
      id: "minerals",
      title: "Mineral Research",
      description:
        "Exploring innovative applications of minerals in corrosion prevention and developing sustainable mineral-based solutions for industrial protection.",
      icon: "🔬",
    },
    {
      id: "cathodic",
      title: "Cathodic Protection",
      description:
        "Advancing cathodic protection technologies through improved anode materials, monitoring systems, and application methods for various environments.",
      icon: "⚡",
    },
    {
      id: "corrosion",
      title: "Corrosion Science",
      description:
        "Studying corrosion mechanisms and developing predictive models to enhance protection strategies for critical infrastructure.",
      icon: "🔍",
    },
  ];

  // Research projects organized by area
  const projects = [
    {
      id: 1,
      area: "minerals",
      title: "Novel Mineral Coatings for Metal Protection",
      description:
        "Developing eco-friendly mineral-based coatings with enhanced corrosion resistance properties.",
      image: "/images/landing/research/research2.jpg",
    },
    {
      id: 2,
      area: "minerals",
      title: "Sustainable Mineral Extraction Methods",
      description:
        "Researching environmentally sustainable approaches to mineral extraction for corrosion protection applications.",
      image: "/images/landing/research/research4.jpg",
    },
    {
      id: 3,
      area: "cathodic",
      title: "New Anode Innovations for Cathodic Protection",
      description:
        "Latest anode materials that are more efficient in protecting metal structures from corrosion.",
      image: "/images/landing/research/research1.jpg",
    },
    {
      id: 4,
      area: "cathodic",
      title: "Remote Monitoring Systems for Cathodic Protection",
      description:
        "Developing IoT-based solutions for real-time monitoring of cathodic protection system performance.",
      image: "/images/landing/research/research3.jpg",
    },
    {
      id: 5,
      area: "corrosion",
      title: "AI in Corrosion Prediction",
      description:
        "Utilizing artificial intelligence to predict corrosion levels based on historical and real-time data.",
      image: "/images/landing/research/research4.jpg",
    },
    {
      id: 6,
      area: "corrosion",
      title: "Material Testing in Extreme Environments",
      description:
        "Testing the performance of protective measures in highly corrosive industrial and marine environments.",
      image: "/images/landing/research/research2.jpg",
    },
  ];

  // Research collaborators section
  const collaborators = [
    { name: "University of Engineering", type: "Academic" },
    { name: "National Corrosion Research Institute", type: "Research" },
    { name: "Global Industrial Solutions", type: "Industry" },
    { name: "Mineral Sciences Laboratory", type: "Research" },
    { name: "International Cathodic Protection Association", type: "Industry" },
  ];

  // Publications section
  const publications = [
    {
      title: "Advanced Corrosion Prediction Using Machine Learning",
      journal: "Journal of Corrosion Science",
      year: 2024,
      area: "corrosion",
    },
    {
      title: "Novel Cathodic Protection Systems for Offshore Structures",
      journal: "International Journal of Marine Engineering",
      year: 2023,
      area: "cathodic",
    },
    {
      title: "Mineral-Based Inhibitors for Enhanced Metal Protection",
      journal: "Materials Science and Engineering",
      year: 2024,
      area: "minerals",
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <Hero
        title="Research & Development"
        subtitle="Innovation and Research for the Future of Corrosion Protection"
        backgroundImage="/images/landing/bg.png"
      />

      {/* Vision & Mission Section */}
      <motion.div
        initial="initial"
        whileInView="animate"
        viewport={{ once: true }}
        variants={animations.fadeInUp}
        className={`${styles.container} ${styles.sectionSpacing}`}
      >
        <SectionHeader title="R&D Vision & Mission" />
        <p className="text-gray-600 text-lg text-center mb-12">
          We are committed to developing innovative solutions in minerals,
          cathodic protection, and corrosion mitigation to enhance industrial
          infrastructure longevity worldwide.
        </p>
      </motion.div>

      {/* Main Research Areas Section */}
      <motion.div
        initial="initial"
        whileInView="animate"
        viewport={{ once: true }}
        variants={animations.fadeInUp}
        className={`${styles.container} ${styles.sectionSpacing}`}
      >
        <SectionHeader title="Main Research Areas" />
        <div className="grid md:grid-cols-3 gap-8 mb-12">
          {researchAreas.map((area) => (
            <motion.div
              key={area.id}
              className="bg-white shadow-lg rounded-lg overflow-hidden"
              whileHover={{ scale: 1.03 }}
              transition={{ duration: 0.3 }}
            >
              <div className="p-6 text-center">
                <div className="text-4xl mb-4">{area.icon}</div>
                <h3 className="text-2xl font-semibold text-gray-800 mb-3">
                  {area.title}
                </h3>
                <p className="text-gray-600">{area.description}</p>
                <Link
                  to={`/research/${area.id}`}
                  className="inline-block mt-4 px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark transition-colors"
                >
                  Learn More
                </Link>
              </div>
            </motion.div>
          ))}
        </div>
      </motion.div>

      {/* Research Projects Section */}
      <motion.div
        initial="initial"
        whileInView="animate"
        viewport={{ once: true }}
        variants={animations.fadeInUp}
        className={`${styles.container} ${styles.sectionSpacing}`}
      >
        <SectionHeader title="Featured Research Projects" />

        {researchAreas.map((area) => (
          <div key={area.id} className="mb-16">
            <h3 className="text-2xl font-semibold text-center mb-8 text-gray-800">
              {area.title} Projects
            </h3>
            <div className="grid md:grid-cols-2 gap-8">
              {projects
                .filter((project) => project.area === area.id)
                .map((project) => (
                  <motion.div
                    key={project.id}
                    className="bg-white shadow-lg rounded-lg overflow-hidden"
                    whileHover={{ scale: 1.03 }}
                    transition={{ duration: 0.3 }}
                  >
                    <img
                      src={project.image}
                      alt={project.title}
                      className="w-full h-52 object-cover"
                    />
                    <div className="p-6">
                      <h3 className="text-xl font-semibold text-gray-800 mb-2">
                        {project.title}
                      </h3>
                      <p className="text-gray-600 mb-4">
                        {project.description}
                      </p>
                      <Link
                        to={`/research/${area.id}/${project.id}`}
                        className="text-primary hover:underline font-medium"
                      >
                        Read More
                      </Link>
                    </div>
                  </motion.div>
                ))}
            </div>
          </div>
        ))}
      </motion.div>

      {/* Collaboration Section */}
      <motion.div
        initial="initial"
        whileInView="animate"
        viewport={{ once: true }}
        variants={animations.fadeInUp}
        className={`${styles.container} ${styles.sectionSpacing}`}
      >
        <SectionHeader title="Research Collaborations" />
        <p className="text-gray-600 text-lg text-center mb-8">
          We collaborate with universities, research institutions, and industry
          partners to advance knowledge in minerals, cathodic protection, and
          corrosion science.
        </p>

        <div className="grid md:grid-cols-3 gap-6 mt-8">
          {collaborators.map((collaborator, index) => (
            <motion.div
              key={index}
              className="bg-white p-6 rounded-lg shadow-md text-center"
              whileHover={{ y: -5 }}
              transition={{ duration: 0.2 }}
            >
              <h3 className="text-lg font-semibold mb-2">
                {collaborator.name}
              </h3>
              <span className="inline-block px-3 py-1 bg-blue-100 text-primary-dark rounded-full text-sm">
                {collaborator.type}
              </span>
            </motion.div>
          ))}
        </div>
      </motion.div>

      {/* Innovation & Technology Section */}
      <motion.div
        initial="initial"
        whileInView="animate"
        viewport={{ once: true }}
        variants={animations.fadeInUp}
        className={`${styles.container} ${styles.sectionSpacing}`}
      >
        <SectionHeader title="Innovation & Technology" />
        <p className="text-gray-600 text-lg text-center mb-12">
          We develop cutting-edge technologies across our three research focus
          areas to advance the field of corrosion protection and infrastructure
          preservation.
        </p>

        <div className="grid md:grid-cols-3 gap-8">
          <div className="bg-white p-6 rounded-lg shadow-md">
            <h3 className="text-xl font-semibold mb-4">
              Mineral Analysis Tools
            </h3>
            <p className="text-gray-600">
              Advanced spectroscopic techniques for rapid characterization of
              mineral properties and applications in corrosion prevention.
            </p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-md">
            <h3 className="text-xl font-semibold mb-4">
              Cathodic Protection Monitoring
            </h3>
            <p className="text-gray-600">
              IoT-enabled remote monitoring systems that provide real-time data
              on cathodic protection performance across distributed
              infrastructure networks.
            </p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-md">
            <h3 className="text-xl font-semibold mb-4">
              AI-Powered Corrosion Analytics
            </h3>
            <p className="text-gray-600">
              Machine learning algorithms analyzing corrosion patterns to
              predict potential issues before they become critical problems,
              saving maintenance costs.
            </p>
          </div>
        </div>
      </motion.div>

      {/* Publications & Articles Section */}
      <motion.div
        initial="initial"
        whileInView="animate"
        viewport={{ once: true }}
        variants={animations.fadeInUp}
        className={`${styles.container} ${styles.sectionSpacing}`}
      >
        <SectionHeader title="Publications & Articles" />
        <p className="text-gray-600 text-lg text-center mb-8">
          Discover our latest research published in journals and industry
          conferences across our three key research areas.
        </p>

        <div className="space-y-4 max-w-3xl mx-auto">
          {publications.map((pub, index) => (
            <motion.div
              key={index}
              className="bg-white p-6 rounded-lg shadow-md"
              whileHover={{ x: 5 }}
              transition={{ duration: 0.2 }}
            >
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="text-lg font-semibold">{pub.title}</h3>
                  <p className="text-gray-600">
                    {pub.journal}, {pub.year}
                  </p>
                  <Link
                    to={`/publications/${index + 1}`}
                    className="text-primary hover:underline inline-block mt-2 text-sm font-medium"
                  >
                    View Publication
                  </Link>
                </div>
                <span className="inline-block px-3 py-1 bg-blue-100 text-primary-dark rounded-full text-sm">
                  {researchAreas.find((area) => area.id === pub.area)?.title ||
                    ""}
                </span>
              </div>
            </motion.div>
          ))}
        </div>

        <div className="text-center mt-8">
          <Link
            to="/publications"
            className="inline-block px-6 py-3 border border-primary text-primary rounded-md hover:bg-primary hover:text-white transition-colors"
          >
            View All Publications
          </Link>
        </div>
      </motion.div>

      {/* CTA Section */}
      <CTASection
        title="Interested in Our Research?"
        subtitle="Contact us for more information about our research in minerals, cathodic protection, and corrosion science."
        buttonText="Contact Us"
        buttonLink="/contact"
      />
    </div>
  );
};

export default Research;
